import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
  IconRefresh,
  IconPlayerPlay,
  IconFileInvoice,
  IconFileOff,
  IconCircleX,
  IconRestore,
} from '@tabler/icons-react';
import { listAllSubscriptions, getUnpaidYears, lineupLabel } from '../services/esetApi';
import type { EsetOverviewRow, EsetUnpaidRow, EsetBillingStatus } from '../services/esetApi';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarSelect,
  ToolbarCheckbox,
  ToolbarSpacer,
  ToolbarCount,
  ToolbarButton,
} from '../components/toolbar';
import { EsetTimelinePanel } from '../components/eset/EsetTimelinePanel';
import { EsetBatchRunDialog } from '../components/eset/EsetBatchRunDialog';
import type { BatchKind } from '../components/eset/EsetBatchRunDialog';
import { EsetReconcileDialog } from '../components/eset/EsetReconcileDialog';
import { EsetRaiseInvoiceDialog } from '../components/eset/EsetRaiseInvoiceDialog';
import type { EsetRaiseInvoiceTarget } from '../components/eset/EsetRaiseInvoiceDialog';
import { EsetVoidInvoiceDialog } from '../components/eset/EsetVoidInvoiceDialog';
import type { EsetVoidInvoiceTarget } from '../components/eset/EsetVoidInvoiceDialog';
import { EsetCancelDialog } from '../components/eset/EsetCancelDialog';
import type { EsetCancelTarget } from '../components/eset/EsetCancelDialog';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'overused', label: 'Extra seats' },
  { value: 'ended', label: 'Cancelled / lapsed' },
];

function formatDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—';
}

type BillingTone = 'success' | 'warning' | 'error' | 'neutral';

/** Service status (the "Status" column): trial · suspended · extra seats (ESET licenses more
 *  units than were billed this term — manage charges them pro rata) · active. Priority top-down. */
function serviceStatus(row: EsetOverviewRow): { key: string; label: string; tone: BillingTone } {
  if (row.billing_status === 'cancelled') return { key: 'ended', label: 'Cancelled', tone: 'neutral' };
  if (row.billing_status === 'lapsed') return { key: 'ended', label: 'Lapsed', tone: 'neutral' };
  if (row.trial) return { key: 'trial', label: 'Trial', tone: 'neutral' };
  if (row.suspended) return { key: 'suspended', label: 'Suspended', tone: 'warning' };
  if (row.billed_units != null && row.licensed_units > row.billed_units)
    return { key: 'overused', label: 'Extra seats', tone: 'error' };
  return { key: 'active', label: 'Active', tone: 'success' };
}

/** The authoritative billing-status chip (backend `billing_status`) → label + tone. `overdue`
 *  = an invoice was raised, unpaid, past due; `awaiting_payment` = raised + unpaid but not yet
 *  past due; `unbilled` = the renewal is due/lapsed with no invoice raised yet (distinct from
 *  overdue). */
const BILLING_STATUS_CHIP: Record<EsetBillingStatus, { label: string; tone: BillingTone }> = {
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  lapsed: { label: 'Lapsed', tone: 'neutral' },
  trial: { label: 'Trial', tone: 'neutral' },
  overdue: { label: 'Overdue', tone: 'error' },
  awaiting_payment: { label: 'Awaiting payment', tone: 'neutral' },
  unbilled: { label: 'Unbilled', tone: 'warning' },
  due_soon: { label: 'Due soon', tone: 'warning' },
  current: { label: 'Current', tone: 'success' },
};

/** Billing health rendered straight from the backend's authoritative `billing_status` (no
 *  client-side derivation). The historical never-billed years (`unpaid_years`) stay a separate,
 *  higher-priority "Unpaid · N yr" chip that opens the reconcile dialog. */
function billingStatus(
  row: EsetOverviewRow,
  unpaid: EsetUnpaidRow | undefined,
): { label: string; tone: BillingTone; unpaid: boolean } {
  if (unpaid) return { label: `Unpaid · ${unpaid.unpaid_years} yr`, tone: 'error', unpaid: true };
  const chip = (row.billing_status && BILLING_STATUS_CHIP[row.billing_status]) ?? {
    label: '—',
    tone: 'neutral' as BillingTone,
  };
  return { label: chip.label, tone: chip.tone, unpaid: false };
}

/** Admin → ESET: every customer with ESET, filterable by name + status (default Active).
 *  Read-only overview; per-customer management stays in the EsetManagePanel slide-over. */
export const EsetIntegrationAdminPage: React.FC = () => {
  const [rows, setRows] = useState<EsetOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState('active'); // default Active
  const [search, setSearch] = useState('');
  const [drill, setDrill] = useState<{ id: string; name: string | null } | null>(null);
  const [batch, setBatch] = useState<BatchKind | null>(null);
  const [unpaidBySub, setUnpaidBySub] = useState<Record<string, EsetUnpaidRow>>({});
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [reconcile, setReconcile] = useState<EsetUnpaidRow | null>(null);
  const [raiseFor, setRaiseFor] = useState<EsetRaiseInvoiceTarget | null>(null);
  const [voidFor, setVoidFor] = useState<EsetVoidInvoiceTarget | null>(null);
  const [cancelFor, setCancelFor] = useState<EsetCancelTarget | null>(null);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setLoadError(null);
      const [all, unpaid] = await Promise.all([
        listAllSubscriptions(signal),
        // Degrade gracefully — the overview still loads if the unpaid-years report errors.
        getUnpaidYears(signal).catch(() => ({ subscriptions: [] as EsetUnpaidRow[] })),
      ]);
      setRows(all.subscriptions ?? []);
      setUnpaidBySub(Object.fromEntries((unpaid.subscriptions ?? []).map((u) => [u.id, u])));
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return;
      setLoadError(e instanceof Error ? e.message : 'Failed to load ESET subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (unpaidOnly && !unpaidBySub[r.id]) return false;
      if (status !== 'all' && serviceStatus(r).key !== status) return false;
      if (!q) return true;
      return [r.customer_name, r.commercial_product, r.product_name, r.product_bundle, r.license_ref]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q));
    });
  }, [rows, status, search, unpaidOnly, unpaidBySub]);

  return (
    <Box sx={{ background: '#f0f4f9', minHeight: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Status"
          value={status}
          onChange={(v) => setStatus(v)}
          options={STATUS_OPTIONS}
        />
        <ToolbarSearch
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search customer, product, bundle, licence…"
        />
        <ToolbarCheckbox
          label="Unpaid years only"
          active={unpaidOnly}
          onClick={() => setUnpaidOnly((v) => !v)}
        />
        <ToolbarSpacer />
        <ToolbarButton icon={IconPlayerPlay} onClick={() => setBatch('sync')}>Run sync</ToolbarButton>
        <ToolbarButton icon={IconPlayerPlay} onClick={() => setBatch('manage')}>Run manage</ToolbarButton>
        <ToolbarCount>{visible.length} of {rows.length}</ToolbarCount>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refresh()} disabled={loading}>
              <IconRefresh size={18} stroke={1.5} />
            </IconButton>
          </span>
        </Tooltip>
      </SecondaryToolbar>

      <Box sx={{ px: '16px', pb: '24px' }}>
        {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTableCard>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Customer</DataTableHeadCell>
                  <DataTableHeadCell>Status</DataTableHeadCell>
                  <DataTableHeadCell>Product</DataTableHeadCell>
                  <DataTableHeadCell>Started</DataTableHeadCell>
                  <DataTableHeadCell align="right">Units (ESET)</DataTableHeadCell>
                  <DataTableHeadCell>Last billed</DataTableHeadCell>
                  <DataTableHeadCell>Next billing</DataTableHeadCell>
                  <DataTableHeadCell>Billing status</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <DataTableEmpty colSpan={8}>No ESET subscriptions match</DataTableEmpty>
                ) : (
                  visible.map((r) => (
                    <DataTableRow key={r.id} onClick={() => setDrill({ id: r.id, name: r.customer_name })}>
                      <DataTableCell bold>{r.customer_name || '—'}</DataTableCell>
                      <DataTableCell>
                        {(() => {
                          const ss = serviceStatus(r);
                          return <Chip tone={ss.tone}>{ss.label}</Chip>;
                        })()}
                      </DataTableCell>
                      <DataTableCell muted>
                        {r.commercial_product
                          ? `${r.commercial_product.replace('ESET PROTECT ', '')} · ${r.billing_class ?? ''}`
                          : r.product_name || '—'}
                        {r.product_needs_review && (
                          <>
                            {' '}
                            <Chip tone="warning">Review</Chip>
                          </>
                        )}
                      </DataTableCell>
                      <DataTableCell muted>{formatDate(r.start_date || r.first_created)}</DataTableCell>
                      <DataTableCell align="right">
                        <span title={lineupLabel(r.lineup) || undefined}>{r.licensed_units ?? r.quantity}</span>
                      </DataTableCell>
                      <DataTableCell muted>{formatDate(r.last_billed)}</DataTableCell>
                      {/* Next billing = the backend's covering-aware renewal date
                          (next_billing = expiry − 30d; a PAST date = due/overdue, unraised). */}
                      <DataTableCell muted>{formatDate(r.next_billing)}</DataTableCell>
                      <DataTableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {(() => {
                            const bs = billingStatus(r, unpaidBySub[r.id]);
                            return (
                              <Chip
                                tone={bs.tone}
                                title={bs.unpaid ? 'Reconcile unbilled years' : undefined}
                                onClick={
                                  bs.unpaid
                                    ? (e) => {
                                        e.stopPropagation();
                                        setReconcile(unpaidBySub[r.id]);
                                      }
                                    : undefined
                                }
                              >
                                {bs.label}
                              </Chip>
                            );
                          })()}
                          {r.billing_status !== 'cancelled' && r.billing_status !== 'lapsed' && (
                          <Tooltip title="Raise invoice">
                            <IconButton
                              size="small"
                              sx={{ color: '#aaa', '&:hover': { color: '#4A90D9' } }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRaiseFor({
                                  id: r.id,
                                  customerName: r.customer_name,
                                  productBundle: r.product_bundle,
                                });
                              }}
                            >
                              <IconFileInvoice size={16} stroke={1.5} />
                            </IconButton>
                          </Tooltip>
                          )}
                          {/* Void: only when an invoice was raised + is unpaid (awaiting/overdue). */}
                          {(r.billing_status === 'awaiting_payment' || r.billing_status === 'overdue') && (
                            <Tooltip title="Void invoice">
                              <IconButton
                                size="small"
                                sx={{ color: '#aaa', '&:hover': { color: '#c0392b' } }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVoidFor({
                                    id: r.id,
                                    customerName: r.customer_name,
                                    productBundle: r.product_bundle,
                                  });
                                }}
                              >
                                <IconFileOff size={16} stroke={1.5} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {r.billing_status !== 'lapsed' && (
                            <Tooltip title={r.cancelled ? 'Reinstate / suspend in ESET' : 'Cancel subscription'}>
                              <IconButton
                                size="small"
                                sx={{ color: '#aaa', '&:hover': { color: r.cancelled ? '#4A90D9' : '#c0392b' } }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCancelFor({
                                    id: r.id,
                                    customerName: r.customer_name,
                                    productBundle: r.product_bundle,
                                    licenseRef: r.license_ref,
                                    cancelled: !!r.cancelled,
                                    removedFromVendor: r.removed_from_vendor ?? null,
                                    trial: r.trial,
                                    suspended: r.suspended,
                                  });
                                }}
                              >
                                {r.cancelled ? (
                                  <IconRestore size={16} stroke={1.5} />
                                ) : (
                                  <IconCircleX size={16} stroke={1.5} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </DataTableCell>
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </DataTable>
          </DataTableCard>
        )}
      </Box>

      <EsetTimelinePanel
        subscriptionId={drill?.id ?? null}
        customerName={drill?.name ?? null}
        onClose={() => setDrill(null)}
      />

      <EsetBatchRunDialog kind={batch} onClose={() => setBatch(null)} onRealRun={() => refresh()} />

      <EsetReconcileDialog
        row={reconcile}
        onClose={() => setReconcile(null)}
        onReconciled={() => refresh()}
      />

      <EsetRaiseInvoiceDialog
        sub={raiseFor}
        onClose={() => setRaiseFor(null)}
        onRaised={() => refresh()}
      />

      <EsetVoidInvoiceDialog
        sub={voidFor}
        onClose={() => setVoidFor(null)}
        onVoided={() => refresh()}
      />

      <EsetCancelDialog
        sub={cancelFor}
        onClose={() => setCancelFor(null)}
        onChanged={() => refresh()}
      />
    </Box>
  );
};
